import React, { type CSSProperties, useRef, useState, useEffect, useCallback } from 'react';
import { type Interaction } from '@lib/models/Interaction';
import MessagePart from './MessagePart';
import { isAskUserPart } from './parts/types';
import { type InteractionPart, InteractionPartUtils } from '@lib/models/InteractionPart';
import { EventType } from '@lib/models/enums';
import { InteractionPartGroup, InteractionService } from '@lib/services/interactionService';
import { EventBusService, MockEventBusService } from '@lib/services/eventBusService.js';
import type { IArtifactService } from '@lib/services/interfaces/artifactService';
import _ from 'lodash';
import { useAiChatServices, useAiChatSessionStore } from '..';
import Stack from '@mui/material/Stack';
import SubAgentBox from './SubAgentBox';
import { useTheme } from '@mui/material/styles';
import MessagePersona, { PERSONA_CONTENT_INDENT } from './MessagePersona';

// UI render tools whose server-generated SlotId is referenced by a
// `{{Component:<id>}}` token in the assistant text are rendered INSIDE that
// text by LlmResponseRender (at the token), so drop them from the positional
// part list here to avoid a double render. uiTool parts NOT referenced by a
// token still render at their stream position (the fallback). No-op otherwise.
function suppressSlottedUiParts(parts: InteractionPart[], onGoing: boolean): InteractionPart[] {
  const isUi = (p: InteractionPart) =>
    (p.eventType === EventType.FunctionCall
      || p.eventType === EventType.FunctionResponse
      || p.eventType === EventType.PartialFunctionCall)
    // ask-user parts are also handled here (parked → hidden inline; answered → shown), so treat them
    // as "UI" for this gate even if a future ask-user tool isn't also [KernelUiFunction].
    && ((p.functionDetails?.isUi ?? false) || isAskUserPart(p.functionDetails));

  // SlotId lives in the tool result — a clean object on the live path, but a
  // (sometimes double-encoded) JSON string once persisted/replayed. uiSlotId
  // unwraps either shape; reading `.SlotId` off the raw string yields undefined.
  const slotOf = (p: InteractionPart) => InteractionPartUtils.uiSlotId(p);

  // Slot ids the model actually placed with a `{{Component:id}}` token in its text
  // (LlmResponseRender renders those inline at the token). Computed from the text
  // streamed SO FAR, so it's valid live too: a token that hasn't arrived yet simply
  // isn't in the set, and its tool keeps rendering at its stream position until it does.
  const referenced = new Set<string>();
  for (const p of parts) {
    if (p.eventType === EventType.Text) {
      const t = InteractionPartUtils.getPartTextContent(p);
      if (t) for (const m of t.matchAll(/\{\{\s*Component:\s*([\w-]+)\s*\}\}/g)) referenced.add(m[1]);
    }
  }

  // While the turn is live: a UI tool whose result is in (status Done) renders NOW,
  // at its stream position — UNLESS the model already placed it with a `{{Component:id}}`
  // token (then it renders inside the text there, avoiding a double render). This makes
  // the placeable components (present_chart / present_metric / present_tsx) appear the
  // moment their result lands instead of staying invisible for the whole turn whenever
  // the model never writes a token; the same rule covers the side-effect ACTION tools
  // (present_dashboard_pad / present_guide / refresh_dashboard, no SlotId → never
  // referenced), whose EnvActionChip useEffect must fire against the REAL environment
  // inside the live <CurrentMessage> (at turn-end they'd only mount under ChatPane's
  // <InertEnvironmentProvider>, where env calls no-op). Parts still streaming
  // (status != Done) stay hidden to avoid a positional flash before the result arrives.
  if (onGoing) {
    return parts.filter((p) => {
      if (!isUi(p)) return true;
      const s = slotOf(p);
      // While parked (status !== 'Done') the ask_user prompt is surfaced by the
      // pane-level AskUserSheet docked above the composer — NOT inline, or it would
      // show in two places. Once answered (Done) it renders inline as the locked
      // transcript card (live and, after the turn closes, in history).
      // ask_user AND askauth are surfaced by the pane-level AskUserSheet docked above the composer while
      // parked (status !== 'Done') — NOT inline, or they'd show in two places. Once answered (Done) they
      // render inline as the locked transcript card (live, and after the turn closes, in history).
      if (isAskUserPart(p.functionDetails)) {
        if (p.functionDetails?.status !== 'Done') return false;
        return !(s && referenced.has(s));
      }
      if (p.functionDetails?.status !== 'Done') return false;
      return !(s && referenced.has(s));
    });
  }

  // Turn complete: drop only the parts whose SlotId a token actually referenced
  // (those render inside the text); any UI tool the model never placed with a
  // token falls back to positional rendering here.
  if (referenced.size === 0) return parts;
  return parts.filter((p) => {
    if (!isUi(p)) return true;
    const s = slotOf(p);
    return !(s && referenced.has(s)); // referenced → rendered inside the text instead
  });
}

//showSubAgents
interface MessageAgentProps {
  message: Interaction;
  onGoing: boolean;
  onLinkClicked: (link: string, content: string | null) => void;
  appendWaitingPart: boolean;
  messagesRef?: React.RefObject<Map<string, HTMLDivElement>>;
  isConnectionOpen: boolean;
  showSubAgents?: boolean;
  preview: boolean;
  container: string,
  friendlyMode?: boolean,
}

const MessageAgent: React.FC<MessageAgentProps> = ({ message, showSubAgents, onGoing, container, preview, onLinkClicked, appendWaitingPart, messagesRef, isConnectionOpen, friendlyMode = false }) => {
  const subAgents = useAiChatSessionStore((state) => state.subAgents);
  const activeSubAgents = [...subAgents.values()];
  const { showToolCalls, showPersona } = useAiChatServices();

  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLessThenMedium, setIsLessThenMedium] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      setIsLessThenMedium(width < theme.breakpoints.values.md);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [theme.breakpoints.values.md]);

  const showSuspended = message.isSuspended === true;

  // Count only agent-side parts for the "nothing yet" loading heuristics — a user turn's
  // injected UserAnswer part (rendered by MessageUser, not here) must not read as agent output.
  const agentPartsCount = (message.parts ?? []).filter((p) => p.eventType !== EventType.UserAnswer).length;

  let traillingPart: InteractionPart | null = null;
  let plannigPart: InteractionPart | null = null;
  if (showSuspended) {
    traillingPart = InteractionPartUtils.createSuspended();
  }
  else {
    traillingPart = InteractionPartUtils.createWaiting(isConnectionOpen && agentPartsCount == 0, false, "", new Date())
  }

  if (appendWaitingPart) {
    if (!message.parts || message.parts.length == 1)
      plannigPart = InteractionPartUtils.fromText("Planejando próximos passos", "system", "planningPart");
  }

  if (!message.asyncFinalResponse) {//stop timers
    if (message.parts.length > 0) {
      const lastPart = message.parts[message.parts.length - 1];
      if (lastPart.waitingDetails)
        lastPart.waitingDetails!.loadDots = true;
    }
  }

  // Use InteractionService to process and group parts
  const interactionService = new InteractionService(message, new MockEventBusService());

  let groups: InteractionPartGroup[] = [];

  if (!message.parts)
    return null;

  // Always strip ToolEvent parts (those are surfaced via the alert icon
  // and the host's task list). Function-call parts (call / response /
  // partial — the runtime mutates a completed call's eventType from
  // FunctionCall to FunctionResponse on the same part, so all three
  // variants need the same treatment) collapse out of view once the
  // turn closes (i.e. when this message is rendered through `Message`
  // with `onGoing={false}`), unless the host opts back into keeping
  // them by setting `showToolCalls`. While the turn is still live
  // (`onGoing` — CurrentMessage) they stay visible so the user can
  // watch the run progress.
  let partsToConsider = suppressSlottedUiParts(message.parts ?? [], onGoing).filter((p) => {
    if (p.eventType === EventType.ToolEvent) return false;
    // A user turn's UI representation renders on the user side (MessageUser), never here.
    if (p.eventType === EventType.UserAnswer) return false;
    // Client-only output siblings are folded into their call part (functionDetails.clientOutput);
    // they never render standalone. Drop any that slipped through as their own part.
    if (p.eventType === EventType.FunctionClientOutput) return false;
    if((
      p.eventType === EventType.FunctionCall
      || p.eventType === EventType.FunctionResponse
      || p.eventType === EventType.PartialFunctionCall
    )) {
      // UI render tools (UI_ prefix) are first-class content — always show them,
      // independent of the showToolCalls toggle. (classifyPart routes them to
      // the `uiTool` kind so the host renders them as components.)
      if (p.functionDetails?.isUi || p.functionDetails?.isAskUser || showToolCalls) return true;
      return showToolCalls;
    }
    return true;
  });

  // const expandedParts: InteractionPart[] = []
  // partsToConsider.forEach((p) => {
  //   if (p.eventType == EventType.Text) {
  //     expandedParts.push(p);
  //   }
  //   else {
  //     expandedParts.push(p);
  //   }
  // })

  groups = interactionService.groupPartsByDisplayType(partsToConsider, onGoing, showToolCalls);


  const getStyleGroup = (group: InteractionPartGroup): CSSProperties => {
    const useColumn = friendlyMode || group.type !== 'inline';
    return {
      display: 'flex',
      flexDirection: useColumn ? 'column' : 'row',
      flexWrap: useColumn ? 'nowrap' : 'wrap',
      gap: useColumn ? undefined : '1px',
      marginBottom: useColumn ? '2px' : '8px',
      alignItems: 'flex-start',
      maxWidth: '100%'
    }
  }
  const getMessagePart = (part: InteractionPart, group: InteractionPartGroup) => {
    const tempPart = onGoing || (!onGoing && part.eventType !== EventType.Thought) ? <MessagePart
      onGoing={onGoing}
      part={part}
      message={message}
      onLinkClicked={onLinkClicked}
      friendlyMode={friendlyMode}
      isLastInlineInGroup={group.lastWithThoughts || (group.isLastGroup)} preview={preview} /> : null;

    if (tempPart) {
      const useBlockStyle = friendlyMode || group.type !== 'inline';
      return <div
        key={`${message.interactionId}-${part.id}`}
        style={useBlockStyle
          ? { width: '100%', marginTop: 1 }
          : { flex: isLessThenMedium ? '1 1 100%' : '1 1 calc(50% - 4px)', maxWidth: isLessThenMedium ? '100%' : 'calc(50% - 4px)', marginTop: 2 }}
      >{tempPart}</div>
    }
    else {
      return null;
    }
  }

  return (
    <Stack>
      <MessagePersona isUser={false} message={message} tone={'light'} />
      <div
        id={`container-iteraction_${message.interactionId}_${container}`}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          fontFamily: theme.typography.fontFamily,
          position: 'relative',
          paddingBottom: 12,
          // Indent the body under the persona label (avatar + gap) so text starts after the avatar.
          paddingLeft: showPersona ? PERSONA_CONTENT_INDENT : undefined,
          color: showSuspended ? theme.palette.text.disabled : theme.palette.text.primary,
        }}
        data-interaction-question={message.interactionQuestion}
        data-interactionid={message.interactionId}

        ref={(el) => {
          (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
          if (el) {
            messagesRef?.current?.set(message.interactionId, el);
          }
        }}
      >
        {plannigPart && (
          <MessagePart onGoing={onGoing} part={plannigPart} message={message} onLinkClicked={onLinkClicked} preview={preview} friendlyMode={friendlyMode} />
        )}
        {groups.map((group, groupIndex) => {
          const groupsToRender = group.parts.map(
            (part) => getMessagePart(part, group)
          ).filter(p => p != null);

          return group.parts.length > 0 && groupsToRender.length > 0 && (
            <div
              key={`group-${groupIndex}-${message.interactionId}-${container}`}
              style={getStyleGroup(group)}
            >
              {groupsToRender}
            </div>)
        })}

        {(appendWaitingPart || showSuspended) && traillingPart &&
          <>
            <MessagePart onGoing={onGoing} part={traillingPart} message={message} onLinkClicked={onLinkClicked} preview={preview} friendlyMode={friendlyMode} />
          </>
        }
        {/* <DelayTextComponent useMarkdown={true} text={'Super texto grande que vai ser escrito parte a parte **para** poder testar como fica tudo Super texto grande que vai ser escrito parte a parte para poder testar como fica tudo Super texto grande que vai ser escrito parte a parte **para** poder testar como fica tudo Super texto grande que vai ser escrito parte a parte para poder testar como fica tudo Super texto grande que vai ser escrito parte a parte **para** poder testar como fica tudo Super texto grande que vai ser escrito parte a parte para poder testar como fica tudo Super texto grande que vai ser escrito parte a parte **para** poder testar como fica tudo Super texto grande que vai ser escrito parte a parte para poder testar como fica tudo Super texto grande que vai ser escrito parte a parte **para** poder testar como fica tudo Super texto grande que vai ser escrito parte a parte para poder testar como fica tudo Super texto grande que vai ser escrito parte a parte **para** poder testar como fica tudo Super texto grande que vai ser escrito parte a parte para poder testar como fica tudo Super texto grande que vai ser escrito parte a parte **para** poder testar como fica tudo Super texto grande que vai ser escrito parte a parte para poder testar como fica tudo'} totalTime={60000} initialDelay={500} /> */}
      </div>
      {/* {showSubAgents && activeSubAgents.filter(s => s.interaction.interactionId == message?.interactionId).map((sa) => (
        <SubAgentBox
          key={sa.id}
          subAgent={sa}
          compact={false}
          onLinkClicked={onLinkClicked}
        />
      ))} */}
    </Stack>




  );
};

export default MessageAgent;
